import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { endpoints, type ChallanListResponse } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The delivery-challan register (§17.7, Rule 55 CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A CHALLAN IS NOT A TAX INVOICE. It carries a taxable value because Rule 55(1)
 * requires one, and none of that is ever output tax. This file names the money
 * fields `…Paise` and stops there; a caller that labels the column "GST" or
 * "amount due" is asserting a liability that does not exist.
 *
 * CANCELLED CHALLANS ARE HIDDEN UNLESS ASKED FOR, and the SERVER does it —
 * `challanListWhere` applies `status: { not: CANCELLED }` whenever no status is
 * sent. That is the QUOTATION/VOID rule, not the invoice/CANCELLED one, and the
 * difference is deliberate: a cancelled invoice keeps a consumed number in a
 * gap-free series and must stay visible, whereas a cancelled challan describes a
 * movement that did not happen. The screen's subtitle has to SAY so, or a
 * cancelled challan looks deleted and the status select is the only way back to
 * it.
 *
 * THE URL IS THE FILTER STATE, exactly as on the other two registers, and here
 * it is not merely a nicety: `routes.challansUnbilled` is literally
 * `/challans?openOnly=true`, so the home screen links IN with a filter already
 * on. Component state would drop it on the first reload, quietly showing the
 * whole book to someone who asked "what have I delivered and not billed?".
 *
 * `q` MATCHES `challan_number` AND `vehicle_number`, AND NOTHING ELSE — not the
 * customer name, which lives in `customer_snapshot`, a text-backed JSON column
 * the server will not query inside (CLAUDE.md). Finding one buyer's challans is
 * `customerId`. The vehicle number is in `q` on purpose: at the gate the paper
 * is with the driver and the lorry number is the only reference anyone has.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * Cache keys for every challan query in the SPA.
 *
 * FOLDED INTO `lib/query-keys.ts` ON 2026-08-16 and re-exported from here so no
 * call site changed. It was declared locally with a comment saying it belonged
 * in the shared factory and was waiting on another change; the shared factory
 * is where a mutation author looks, and a hand-kept list of entities that omits
 * one is how a write leaves a stale badge on a screen it never imported.
 *
 * The key VALUES are byte-identical to what this file used to build, so nothing
 * cached was orphaned by the move.
 */
export const challanKeys = queryKeys.challans;

/**
 * Fifty a page.
 *
 * The API's own default is 25 and its ceiling is 100
 * (`CHALLAN_LIST_DEFAULT_LIMIT` / `CHALLAN_LIST_MAX_LIMIT`), so `limit` is always
 * sent explicitly rather than left to the server — and 50 is what both existing
 * registers use, which is the whole argument for it. Sending 200, the INVOICE
 * register's ceiling, is a 400 on this endpoint.
 */
export const CHALLAN_PAGE_SIZE = 50;

/**
 * The columns the API will sort by. A closed set — `ChallanListQuerySchema` is
 * `.strict()` and anything else is a 400.
 *
 * NOTE `type`, NOT `challanType`. The FILTER parameter is `challanType`; the
 * SORT parameter is `type`. They are two different keys on the same query
 * object and the schema refuses the wrong one.
 */
export type ChallanSortKey = "number" | "date" | "customer" | "type" | "status" | "total";

const CHALLAN_SORT_KEYS: readonly ChallanSortKey[] = [
  "number",
  "date",
  "customer",
  "type",
  "status",
  "total",
];

export interface ChallanFilters {
  /** `OPEN` | `RETURNED` | `INVOICED` | `CANCELLED`. Naming one un-hides it. */
  status?: string;
  /** The Rule 55(1) reason for the movement. The sort key is `type`. */
  challanType?: string;
  /**
   * "Delivered, and not yet billed" — §17.7's payoff, and NOT `status=OPEN`.
   *
   * Server-side it means OPEN *and* `challanType ∈ {SUPPLY_NOT_INVOICED,
   * APPROVAL}` *and* no linked invoice. A returnable challan for scaffolding is
   * OPEN for as long as the scaffolding is on site and is not money owed; a
   * count that mixes those in cries wolf, and a signal nobody trusts costs
   * exactly the thing the feature was for. Do not label the control "Open
   * challans" — a status select set to OPEN answers a different question, and
   * offering both under similar words is how the signal stops being believed.
   *
   * ONLY EVER `true` OR ABSENT. See {@link useChallanFilters}.
   */
  openOnly?: boolean;
  /**
   * "Still out, and should have been back by now" — returnable, OPEN, and past
   * the expected return date, with a `<` boundary applied SERVER-side so goods
   * due back today are not yet late.
   *
   * ONLY EVER `true` OR ABSENT.
   */
  overdueOnly?: boolean;
  customerId?: number;
  /**
   * Free text over the challan's OWN references — `challan_number` and
   * `vehicle_number`. NOT the customer name; that is `customerId`.
   */
  q?: string;
  /** `YYYY-MM-DD`, INCLUSIVE, on `challan_date`. */
  from?: string;
  to?: string;
  /**
   * INTEGER PAISE, inclusive, on `grand_total_paise` — which IS the figure the
   * row prints here. A challan has no `tax_display_mode` (the column exists on
   * `model Quotation` alone), so unlike the quotation register there is no
   * second printed total these bounds could disagree with.
   *
   * THAT FIGURE IS THE RULE 55(1) DECLARED VALUE OF GOODS IN TRANSIT, NOT MONEY
   * ANYONE OWES. Narrowing by it answers "which loads were worth over a lakh",
   * never "who owes me over a lakh" — do not label the control as anything that
   * asserts a liability.
   */
  minTotalPaise?: number;
  maxTotalPaise?: number;
  /** Column sorting — in the URL, so a sorted view is linkable. */
  sort?: ChallanSortKey;
  dir?: "asc" | "desc";
  offset: number;
}

export function useChallanFilters(): {
  filters: ChallanFilters;
  setFilters: (patch: Partial<ChallanFilters>) => void;
  clearFilters: () => void;
  activeCount: number;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<ChallanFilters>(() => {
    const status = params.get("status") ?? undefined;
    const challanType = params.get("challanType") ?? undefined;
    const rawCustomer = params.get("customerId");
    const customerId = rawCustomer === null || rawCustomer === "" ? undefined : Number(rawCustomer);
    const rawOffset = params.get("offset");
    const q = params.get("q") ?? undefined;

    /** A bound that is not a whole number is no filter — never send `NaN`. */
    const paise = (raw: string | null): number | undefined => {
      if (raw === null || raw.trim() === "") return undefined;
      const value = Number(raw);
      return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
    };

    return {
      status: status === "" ? undefined : status,
      challanType: challanType === "" ? undefined : challanType,
      q: q === "" ? undefined : q,
      // A junk `?customerId=abc` becomes "no filter" rather than `NaN`, which
      // would serialise into the request and earn a 400 the owner cannot read.
      customerId: customerId === undefined || !Number.isFinite(customerId) ? undefined : customerId,
      /*
       * `true` OR `undefined`, NEVER `false`, and that is the whole trick to
       * these two.
       *
       * `challanListWhere` only branches on `=== true`, so `openOnly=false` is a
       * no-op that nonetheless rides in the query string, changes the TanStack
       * query key (splitting the cache for two identical result sets) and — the
       * visible part — makes `activeCount` claim a filter is on when the
       * register is showing everything. `BooleanQuerySchema` is also narrow:
       * only "true" | "false" | "1" | "0", so a careless `String(undefined)`
       * reaching the wire is a 400.
       *
       * This is NOT the bill register's `issuedManually`, where `false` is a
       * genuine third answer ("show me only the ones the system numbered").
       * Here there is no question that `false` asks.
       */
      openOnly: params.get("openOnly") === "true" ? true : undefined,
      overdueOnly: params.get("overdueOnly") === "true" ? true : undefined,
      from: params.get("from") ?? undefined,
      to: params.get("to") ?? undefined,
      minTotalPaise: paise(params.get("minTotalPaise")),
      maxTotalPaise: paise(params.get("maxTotalPaise")),
      // A hand-edited `?sort=whatever` degrades to no sort rather than a 400.
      sort: CHALLAN_SORT_KEYS.find((key) => key === params.get("sort")),
      dir: params.get("dir") === "asc" ? "asc" : params.get("dir") === "desc" ? "desc" : undefined,
      offset: rawOffset === null ? 0 : Math.max(0, Number(rawOffset) || 0),
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<ChallanFilters>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        /*
         * `false` CLEARS, exactly as `undefined` and `""` do — the one place
         * this diverges from the quotation register's `setFilters`, and the
         * reason is above: no boolean on this endpoint has a meaningful `false`,
         * so un-ticking "unbilled only" must REMOVE the key rather than write
         * `openOnly=false` into a link the owner may send to someone.
         */
        if (value === undefined || value === "" || value === false) next.delete(key);
        else next.set(key, String(value));
      }
      /*
       * ANY FILTER CHANGE RETURNS TO PAGE ONE. Narrowing the register while
       * still sitting at offset 150 shows an empty page, which reads as
       * "nothing matches" when in fact three things do.
       */
      if (!("offset" in patch)) next.delete("offset");
      // `replace` so a filter fiddle does not stack twenty history entries
      // between the owner and the screen he came from.
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const activeCount = [
    filters.status,
    filters.challanType,
    filters.openOnly,
    filters.overdueOnly,
    filters.customerId,
    filters.q,
    // A range counts once, however many ends it has — "2 filters" for one date
    // range would make the badge read as more than is actually on.
    filters.from ?? filters.to,
    filters.minTotalPaise ?? filters.maxTotalPaise,
  ].filter((value) => value !== undefined).length;

  return { filters, setFilters, clearFilters, activeCount };
}

/**
 * `GET /challans` → `{ data, total, limit, offset }` — envelope key `data`, like
 * quotations, UNLIKE `/invoices` which answers `invoices`.
 *
 * A 404 FROM THIS QUERY IS NOT "no such challan". Every route in
 * `apps/api/src/routes/challans.ts` sits behind `requireFlag("challans")`, which
 * runs BEFORE `requireCap` and answers 404 rather than 403 so that "forbidden"
 * cannot leak the existence of unreleased work (§18.3). There is no `useFlag` on
 * the client and this hook does not invent one — the caller must simply treat a
 * 404 as "the feature is off" and render the not-available state, never "you do
 * not have permission", which is exactly the leak §18.3 exists to prevent.
 */
export function useChallanList(filters: ChallanFilters): UseQueryResult<ChallanListResponse> {
  const query = {
    status: filters.status,
    challanType: filters.challanType,
    // Never `false`: see the note in `useChallanFilters`.
    openOnly: filters.openOnly === true ? true : undefined,
    overdueOnly: filters.overdueOnly === true ? true : undefined,
    customerId: filters.customerId,
    q: filters.q,
    from: filters.from,
    to: filters.to,
    // A key in `ChallanFilters` but not in THIS object is a filter that shows in
    // the URL and does nothing — the register would look narrowed and return the
    // whole book.
    minTotalPaise: filters.minTotalPaise,
    maxTotalPaise: filters.maxTotalPaise,
    limit: CHALLAN_PAGE_SIZE,
    sort: filters.sort,
    dir: filters.dir,
    offset: filters.offset === 0 ? undefined : filters.offset,
  };

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();

  return useQuery({
    queryKey: challanKeys.list(query),
    queryFn: () => api.get<ChallanListResponse>(`${endpoints.challans}${qs === "" ? "" : `?${qs}`}`),
    placeholderData: keepPreviousData,
    /*
     * Thirty seconds, as on the quotation register. A challan moves under the
     * screen in ways the screen did not cause — the goods come back, an invoice
     * is raised against it and the status flips to INVOICED — so this list goes
     * stale by events elsewhere rather than only by edits made here.
     */
    staleTime: 30_000,
  });
}
